import React, { useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'

const { logger } = Sentry;
import { undoDeleteAccount } from '../../services/api'
import './AccountDeletionWarning.scss'

interface AccountDeletionWarningProps {
  user: {
    deleted?: boolean
    delete_date?: string
  } | null
}

function AccountDeletionWarning({ user }: AccountDeletionWarningProps) {
  const { getAccessTokenSilently } = useAuth0()
  const [isUndoing, setIsUndoing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dismissed, setDismissed] = useState(false)

  if (!user?.deleted || !user?.delete_date || dismissed) {
    return null
  }

  // Calculate days until deletion
  const deleteDate = new Date(user.delete_date)
  const now = new Date()
  const daysUntilDeletion = Math.ceil((deleteDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

  if (daysUntilDeletion <= 0) {
    // Account should have been deleted by scheduled job
    return null
  }

  const handleUndo = async () => {
    setIsUndoing(true)
    setError(null)

    try {
      await undoDeleteAccount(getAccessTokenSilently)
      setDismissed(true)
      // Optionally reload the page or refresh user data
      window.location.reload()
    } catch (err) {
      logger.error('Error undoing account deletion', {
        error: err instanceof Error ? err.message : String(err),
      });
      const errorMessage = err instanceof Error ? err.message : 'Failed to undo deletion. Please try again.'
      setError(errorMessage)
      setIsUndoing(false)
    }
  }

  return (
    <div className="account-deletion-warning alert alert-warning" role="alert">
      <div className="d-flex align-items-start">
        <div className="flex-grow-1">
          <h5 className="alert-heading mb-2">
            <strong>Account Scheduled for Deletion</strong>
          </h5>
          <p className="mb-2">
            Your account is scheduled to be deleted in <strong>{daysUntilDeletion} day{daysUntilDeletion !== 1 ? 's' : ''}</strong>.
            All your stored data and remaining credits will be permanently lost.
          </p>
          {error && (
            <div className="alert alert-danger mt-2 mb-2" role="alert">
              {error}
            </div>
          )}
          <button
            className="btn btn-primary btn-sm me-2"
            onClick={handleUndo}
            disabled={isUndoing}
          >
            {isUndoing ? 'Undoing...' : 'Undo Deletion'}
          </button>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setDismissed(true)}
            disabled={isUndoing}
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  )
}

export default AccountDeletionWarning
